import { getTheme } from '@fluentui/react';
import type { ITheme } from '@fluentui/react';
import { PBTCaseStatus } from '../Interfaces/IPBTCase';

const baseUrl = __PBT_SERVICE_URL__;
const theme: ITheme = getTheme();

export const PBTApiEndpoints = {
  Cases: `${baseUrl}/api/v1/pbt/cases`,
  CasesPage: (opts: {
    pageNumber?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    activeOnly?: boolean;
    crmCase?: string;
    managerName?: string;
    managerAlias?: string;
    engagementModel?: string;
    dataStatus?: string;
    termConvDate?: string;
    effTermDate?: string;
    flaggedOnly?: boolean;
    includeSummary?: boolean;
  } = {}) => {
    const {
      pageNumber = 1,
      pageSize = 10,
      search = '',
      status = '',
      activeOnly = false,
      crmCase = '',
      managerName = '',
      managerAlias = '',
      engagementModel = '',
      dataStatus = '',
      termConvDate = '',
      effTermDate = '',
      flaggedOnly = false,
      includeSummary = true,
    } = opts;
    const params = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
    });
    if (search.trim().length >= 3) params.set('search', search.trim());
    if (status) params.set('status', status);
    if (activeOnly) params.set('activeOnly', 'true');
    if (crmCase.trim()) params.set('crmCase', crmCase.trim());
    if (managerName.trim()) params.set('managerName', managerName.trim());
    if (managerAlias.trim()) params.set('managerAlias', managerAlias.trim());
    if (engagementModel) params.set('engagementModel', engagementModel);
    if (dataStatus) params.set('dataStatus', dataStatus);
    if (termConvDate) params.set('termConvDate', termConvDate);
    if (effTermDate) params.set('effTermDate', effTermDate);
    if (flaggedOnly) params.set('flaggedOnly', 'true');
    if (!includeSummary) params.set('includeSummary', 'false');
    return `${baseUrl}/api/v1/pbt/cases?${params.toString()}`;
  },
  CaseDetail: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}`,
  CreateCase: `${baseUrl}/api/v1/pbt/cases`,
  BulkCreateCase: `${baseUrl}/api/v1/pbt/cases/bulk`,
  ExcelBulkUpload: `${baseUrl}/api/v1/pbt/cases/bulk/excel`,
  ExportCases: `${baseUrl}/api/v1/pbt/cases/export`,
  UpdateCase: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}`,
  CaseAudit: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}/audit`,
  PauseCase: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}/pause`,
  ResumeCase: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}/resume`,
  CancelCase: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}/cancel`,
  CRMIncoming: (pageNumber = 1, pageSize = 15, search = '') => {
    const params = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
    });
    if (search.trim()) params.set('search', search.trim());
    return `${baseUrl}/api/v1/pbt/crm-incoming?${params.toString()}`;
  },
  LookupOptions: (category: string) => `${baseUrl}/api/v1/pbt/lookups/${category}`,
  UpdateEngagementModel: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}/engagement-model`,
  EmployeeData: (personnelNumber: string, pbtCaseId?: number) =>
    `${baseUrl}/api/v1/pbt/employee-data/${personnelNumber}${pbtCaseId ? `?pbtCaseId=${pbtCaseId}` : ''}`,
  EmployeeDataOverrides: (pbtCaseId: number) => `${baseUrl}/api/v1/pbt/employee-data/overrides/${pbtCaseId}`,
  SaveEmployeeDataOverrides: `${baseUrl}/api/v1/pbt/employee-data/overrides`,
  UpdateTermConversationDate: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}/term-conversation-date`,
  SendReminder: (id: number) => `${baseUrl}/api/v1/pbt/reminders/${id}`,
  RetryTermEmail: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}/retry-term-email`,
  RetryTerminationPacket: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}/retry-termination-packet`,
  ProcessReminders: `${baseUrl}/api/v1/pbt/reminders/process`,
  Notifications: `${baseUrl}/api/v1/pbt/notifications`,
  MarkNotificationRead: (id: number) => `${baseUrl}/api/v1/pbt/notifications/${id}/read`,
  MarkAllNotificationsRead: `${baseUrl}/api/v1/pbt/notifications/read-all`,
  TermConversation: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}/term-conversation`,
  HrcValidationProceed: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}/hrc-validation/proceed`,
  HrcValidationProceedStatus: (id: number) => `${baseUrl}/api/v1/pbt/cases/${id}/hrc-validation/proceed-status`,
  SecurityFeatureConfig: `${baseUrl}/api/v1/security/feature-config`,
  EmailTemplates: `${baseUrl}/api/v1/pbt/email-templates`,
  EmailTemplateByCode: (code: string) => `${baseUrl}/api/v1/pbt/email-templates/${encodeURIComponent(code)}`,
  EmailTemplateVariables: `${baseUrl}/api/v1/pbt/email-templates/variables`,
  EmailTemplatePreview: (code: string) => `${baseUrl}/api/v1/pbt/email-templates/${encodeURIComponent(code)}/preview`,
} as const;

export const PBTMessages = {
  CasesError: 'Something went wrong while retrieving PBT cases.',
  CaseRefreshError: 'Case details could not be refreshed. The displayed information may be out of date.',
  CRMError: 'Something went wrong while syncing CRM cases.',
  CRMAuthorizationError:
    'You do not have permission to view incoming CRM cases. Contact your administrator if you need access.',
  CreateSuccess: 'PBT case created successfully.',
  CreateError: 'Something went wrong while creating the PBT case.',
  BulkCreateError: 'Something went wrong during bulk case creation.',
  ExcelUploadError: 'Something went wrong while uploading the Excel file.',
  ExportSuccess: 'Case data exported to Excel successfully.',
  ExportError: 'Something went wrong while exporting case data to Excel.',
  UpdateSuccess: 'Case updated successfully.',
  UpdateError: 'Something went wrong while updating the case.',
  PauseSuccess: 'Case paused successfully.',
  PauseError: 'Something went wrong while pausing the case.',
  ResumeSuccess: 'Case resumed successfully.',
  ResumeError: 'Something went wrong while resuming the case.',
  CancelSuccess: 'Case cancelled successfully.',
  CancelError: 'Something went wrong while cancelling the case.',
  CaseActionNotesTooShort: (minLength: number) =>
    `Please provide at least ${minLength} characters explaining the reason.`,
  ReminderSendSuccess: 'Reminder email sent successfully.',
  ReminderSendError: 'Something went wrong while sending the reminder.',
  TermEmailRetrySuccess: 'Termination email re-sent. Delivery is processing in the background.',
  TermEmailRetryNothing: 'No failed termination emails to re-send.',
  TermEmailRetryError: 'Something went wrong while re-sending the termination email.',
  TermEmailRetryAllFailed: 'Re-send failed for all recipients. Please review and try again.',
  TermEmailRetryPartial: (successCount: number, failureCount: number) =>
    `Re-send succeeded for ${successCount} recipient(s) but failed for ${failureCount}. Please review and try again.`,
  PacketRetrySuccess: 'Termination packet build re-queued. It will process in the background; refresh to see the updated status.',
  PacketRetryError: 'Something went wrong while retrying the termination packet build. Please try again.',
  ReminderNotNeeded: 'Term Conversation Date is already set. No reminder needed.',
  ReminderKickoffNotSent: 'The kickoff (manager engagement) email has not been sent yet. Send the kickoff email before sending a reminder.',
  ReminderManagerBypassed: 'Manager is bypassed for this case, so reminders are sent to the assigned HRC instead of the manager.',
  ReminderAlreadyFlagged: 'Manager has been flagged as Non-Responsive. No more reminders will be sent.',
  NotificationsError: 'Something went wrong while retrieving notifications.',
  ProceedSuccess: 'HRC validation completed. The case has been released to continue.',
  ProceedProcessing: 'Proceed was accepted. Documents and emails are processing in the background.',
  ProceedPreparing: 'The HRC validation packet is still being prepared. Finishing up automatically\u2026',
  ProceedError: 'Something went wrong while releasing the HRC validation gate.',
  ManagerFlaggedComplexity:
    'There is a complexity that was flagged. Pause progression and connect with your aligned HR partner or reach out to askHR@microsoft.com before proceeding.',
} as const;

/**
 * Converts a raw reminder result message from the API into a user-friendly message.
 *
 * The backend embeds the internal case identifier in these messages (e.g.
 * "CaseId=42 kickoff email has not been sent yet."). Surfacing that raw
 * "CaseId=NN" token to end users is confusing, so this replaces it with the
 * employee's name. Technical error strings (prefixed with "ERROR") are hidden
 * behind a generic message so internal details are never shown to users.
 */
export const humanizeReminderMessage = (message: string | undefined | null, employeeName?: string): string => {
  if (!message || /^\s*ERROR\b/i.test(message)) {
    return PBTMessages.ReminderSendError;
  }
  const name = employeeName?.trim() || 'the employee';
  const humanized = message.replace(/CaseId\s*=\s*\d+/gi, name).trim();
  return humanized || PBTMessages.ReminderSendError;
};

// Status color mapping for badges — using WCAG AA-compliant dark text on light backgrounds
export const PBTStatusColors: Record<PBTCaseStatus, { background: string; color: string }> = {
  'Pending': { background: '#faf9f8', color: '#605e5c' },
  'Data Gathering': { background: '#deecf9', color: '#005a9e' },
  'Date & Attestation Pending': { background: '#eae4f6', color: '#4b386d' },
  'Awaiting HRC Approval': { background: '#eae4f6', color: '#4b386d' },
  'Preparing Termination Packet': { background: '#eae4f6', color: '#4b386d' },
  'Complete': { background: '#dff6dd', color: '#0b6a0b' },
  'Delivery Failed': { background: '#fde7e9', color: '#a4262c' },
  'Paused': { background: '#fff4ce', color: '#8a6700' },
  'Cancelled': { background: '#f3f2f1', color: '#605e5c' },
  'Removed': { background: '#f3f2f1', color: '#605e5c' },
};

/**
 * Display metadata for a case's status pill. Kept separate from the raw server status so we can
 * (a) show a truthful pill for a Complete case whose termination email later bounced — offboarding
 * is actually on hold until every audit is 'Sent' — and (b) distinguish that post-send bounce from
 * a pre-send terminal failure, so the HRC can tell "we couldn't send it" from "we sent it but it
 * came back". Purely cosmetic: no backend status is changed and the Re-send / offboarding-hold flow
 * is untouched. `colorKey` indexes PBTStatusColors; `label` is the pill text; `tooltip` explains the
 * cause and next action on hover.
 */
export interface CaseStatusDisplay {
  label: string;
  colorKey: string;
  tooltip?: string;
}

export const getCaseStatusDisplay = (status: string, emailFailed?: boolean): CaseStatusDisplay => {
  // Sent, then bounced: workflow finished (status stays Complete) but the recipient never got the
  // email, so offboarding is held. Surface it distinctly from a pre-send failure.
  if (emailFailed === true && status === PBTCaseStatus.Complete) {
    return {
      label: 'Email Bounced',
      colorKey: PBTCaseStatus.DeliveryFailed,
      tooltip:
        'The termination email was sent but came back undeliverable — the recipient did not receive it. Offboarding is on hold until it is delivered. Re-send the termination email from the case.',
    };
  }
  // Pre-send terminal failure: the packet/email could not be sent at all.
  if (status === PBTCaseStatus.DeliveryFailed) {
    return {
      label: PBTCaseStatus.DeliveryFailed,
      colorKey: PBTCaseStatus.DeliveryFailed,
      tooltip:
        'The termination packet/email could not be sent (a terminal error occurred before delivery). Offboarding is on hold. Retry from the case.',
    };
  }
  return { label: status, colorKey: status };
};

export const PBTFlagColors: Record<string, { background: string; color: string; icon: string }> = {
  'GER/WIT': { background: '#fde7e9', color: '#a4262c', icon: 'BlockedSolid' },
  'LOA': { background: '#fff4ce', color: '#6b5700', icon: 'Warning' },
  'Overdue': { background: '#fed9cc', color: '#8a3707', icon: 'Clock' },
  'CRM Mismatch': { background: '#f3e8fd', color: '#5c2d91', icon: 'AlertSolid' },
  'Non-Responsive': { background: theme.palette.neutralLighter, color: theme.palette.neutralPrimary, icon: 'MailPause' },
  'Email Failed': { background: '#fde7e9', color: '#a4262c', icon: 'MailAlert' },
};

// Stat card configuration — using theme palette tokens (dark variants for WCAG AA contrast)
export const PBTStatCardConfig = [
  { key: 'totalActive', label: 'Total Active', colorToken: theme.palette.tealDark, gradientFrom: theme.palette.teal, gradientTo: theme.palette.tealLight },
  { key: 'dataGathering', label: 'Data Gathering', colorToken: theme.palette.themeDarkAlt, gradientFrom: theme.palette.themePrimary, gradientTo: theme.palette.themeLight },
  { key: 'awaiting', label: 'Date & Attestation Pending', colorToken: theme.palette.purpleDark, gradientFrom: theme.palette.purple, gradientTo: theme.palette.purpleLight },
  { key: 'complete', label: 'Complete', colorToken: theme.palette.greenDark, gradientFrom: theme.palette.green, gradientTo: theme.palette.greenLight },
] as const;

// ── Dropdown option configs (used by BulkEntryPanel & DetailsEntryPanel) ──────

export const PBTOutcomeOptions = [
  { key: 'INVOL-Performance', text: 'INVOL — Performance' },
  { key: 'INVOL-Policy Violation', text: 'INVOL — Policy Violation' },
  { key: 'INVOL-Position Elimination', text: 'INVOL — Position Elimination' },
  { key: 'INVOL-Failed Contingency', text: 'INVOL — Failed Contingency' },
  { key: 'INVOL-Just Cause', text: 'INVOL — Just Cause' },
  { key: 'VOL-Job Abandonment', text: 'VOL — Job Abandonment' },
] as const;

export const PBTRehireOptions = [
  { key: 'Eligible External Staff Only - 2 yr', text: 'Eligible External Staff Only - 2 yr' },
  { key: 'Eligible for Rehire', text: 'Eligible for Rehire' },
] as const;

// ── Employee-data manual-override dropdowns (Employee Data tab) ────────────────
// Immigration Status is a simple Yes/No indicating whether the employee is subject to
// immigration handling. It is persisted to the backend `ImmigrationStatus` override field.
export const PBTImmigrationStatusOptions = [
  { key: 'Yes', text: 'Yes' },
  { key: 'No', text: 'No' },
] as const;

// Visa Type is the detailed I-94 status label. Values mirror the MIMS
// GetI94ImmigrationStatus option-set labels (HRS-MIMS API spec). The selected label is
// persisted to the backend `VisaType` override field.
export const PBTVisaTypeOptions = [
  { key: 'USC', text: 'USC — U.S. Citizen' },
  { key: 'PR', text: 'PR — Permanent Resident (Green Card)' },
  { key: 'H-1B', text: 'H-1B — Specialty Occupation Worker' },
  { key: 'H-4', text: 'H-4 — Dependent of H-1B' },
  { key: 'L-1', text: 'L-1 — Intracompany Transferee' },
  { key: 'L-1A', text: 'L-1A — Manager/Executive' },
  { key: 'L-1B', text: 'L-1B — Specialized Knowledge' },
  { key: 'L-2', text: 'L-2 — Dependent of L-1' },
  { key: 'L-2S', text: 'L-2S — Work Authorized Dependent' },
  { key: 'L-2Y', text: 'L-2Y — Work Authorized Dependent (Yes)' },
  { key: 'TN', text: 'TN — NAFTA Professional' },
  { key: 'Mexican TN', text: 'Mexican TN — TN Status for Mexican Citizens' },
  { key: 'Canadian TN', text: 'Canadian TN — TN Status for Canadian Citizens' },
  { key: 'TD', text: 'TD — Dependent of TN' },
  { key: 'Intermittent L-1', text: 'Intermittent L-1' },
  { key: 'Intermittent TN', text: 'Intermittent TN' },
  { key: 'E-3', text: 'E-3 — Australian Specialty Occupation' },
  { key: 'E-3 Dependent', text: 'E-3 Dependent' },
  { key: 'E-3S', text: 'E-3S — Principal' },
  { key: 'E-3Y', text: 'E-3Y — Principal (Yes)' },
  { key: 'E-3DS', text: 'E-3DS — Dependent' },
  { key: 'E-3DY', text: 'E-3DY — Dependent (Yes)' },
  { key: 'Chilean H-1B1', text: 'Chilean H-1B1 — Professional' },
  { key: 'Singaporean H-1B1', text: 'Singaporean H-1B1 — Professional' },
  { key: 'F-1', text: 'F-1 — Student' },
  { key: 'F-2', text: 'F-2 — Dependent of F-1' },
  { key: 'F-1 Intern', text: 'F-1 Intern' },
  { key: 'F-1 Researcher', text: 'F-1 Researcher' },
  { key: 'J-1', text: 'J-1 — Exchange Visitor' },
  { key: 'J-1 AWT', text: 'J-1 AWT — Academic Work Training' },
  { key: 'J-1 Intern', text: 'J-1 Intern' },
  { key: 'J-1 Researcher', text: 'J-1 Researcher' },
  { key: 'J-1 Trainee', text: 'J-1 Trainee' },
  { key: 'J-2', text: 'J-2 — Dependent of J-1' },
  { key: 'O-1', text: 'O-1 — Extraordinary Ability' },
  { key: 'O-2', text: 'O-2 — Support Personnel for O-1' },
  { key: 'O-3', text: 'O-3 — Dependent of O-1/O-2' },
  { key: 'B-1', text: 'B-1 — Business Visitor' },
  { key: 'B-2', text: 'B-2 — Tourist Visitor' },
  { key: 'H-3', text: 'H-3 — Trainee' },
  { key: 'K-1', text: 'K-1 — Fiancé(e)' },
  { key: 'K-2', text: 'K-2 — Dependent of K-1' },
  { key: 'K-3', text: 'K-3 — Spouse of U.S. Citizen' },
  { key: 'K-4', text: 'K-4 — Dependent of K-3' },
  { key: 'G-4', text: 'G-4 — International Organization Employee' },
  { key: 'A-2', text: 'A-2 — Foreign Government Official' },
  { key: 'E-1 Dependent', text: 'E-1 Dependent — Treaty Trader Dependent' },
  { key: 'E-2 Dependent', text: 'E-2 Dependent — Treaty Investor Dependent' },
  { key: 'Asylee', text: 'Asylee' },
  { key: 'Pending Asylum', text: 'Pending Asylum' },
  { key: 'Refugee', text: 'Refugee' },
  { key: 'Pending Adjustment', text: 'Pending Adjustment of Status' },
  { key: 'CPR', text: 'CPR — Conditional Permanent Resident' },
  { key: 'DACA', text: 'DACA — Deferred Action for Childhood Arrivals' },
  { key: 'TPS', text: 'TPS — Temporary Protected Status' },
  { key: 'UHP', text: 'UHP — Unaccompanied Humanitarian Parolee' },
  { key: 'HCap Candidate', text: 'HCap Candidate — H-1B Cap Candidate (Lottery)' },
  { key: 'CFA/FSM', text: 'CFA/FSM — Citizen of Federated States of Micronesia' },
  { key: 'CFA/MIS', text: 'CFA/MIS — Citizen of Marshall Islands' },
  { key: 'Outside the U.S.', text: 'Outside the U.S.' },
] as const;

// Work State override options are the SINGLE SOURCE in backend configuration (appsettings
// "WorkStateOptions"), served to the dropdown via the lookups/WorkState endpoint so the list can
// change without a UI code change or redeploy. This empty array is only the fallback the dropdown
// shows if that request fails or is still loading — deliberately not a duplicate of the state list.
export const PBTWorkStateOptions: readonly { key: string; text: string }[] = [];

// Default Rehire Eligibility for new/blank case entry (must match a PBTRehireOptions key
// and the dbo.LPMLookup RehireEligibility value with SortOrder 1).
export const PBTDefaultRehireEligibility = 'Eligible External Staff Only - 2 yr';

// Lookup categories driving DB-backed dropdowns (must match dbo.LPMLookup.LookupCategory
// and the backend TerminationDetailsValidator). See usePbtLookupOptions.
export const PBTLookupCategories = {
  CrmOutcome: 'CrmOutcome',
  RehireEligibility: 'RehireEligibility',
  EngagementModel: 'EngagementModel',
  WorkState: 'WorkState',
} as const;

// Config-driven dropdown field definitions for entry panels
export const PBTEntryDropdownFields = [
  { key: 'engModel', label: 'Engagement Model', placeholder: 'Select engagement model', required: true, optionsSource: 'engModel' },
  { key: 'outcome', label: 'CRM Case Outcome', placeholder: 'Select outcome', required: true, optionsSource: 'outcome' },
  { key: 'rehire', label: 'Rehire Eligibility', placeholder: 'Select rehire decision', required: true, optionsSource: 'rehire' },
] as const;

// CRM Summary Grid column configuration
export const CRMSummaryGridColumns = [
  { key: 'employeeName', name: 'Employee Name', fieldName: 'employeeName', minWidth: 120, maxWidth: 160 },
  { key: 'personnelNumber', name: 'PERN', fieldName: 'personnelNumber', minWidth: 80, maxWidth: 110 },
  { key: 'employeeAlias', name: 'Employee Alias', fieldName: 'employeeAlias', minWidth: 100, maxWidth: 130 },
  { key: 'managerName', name: 'Manager Name', fieldName: 'managerName', minWidth: 120, maxWidth: 160 },
  { key: 'managerAlias', name: 'Manager Alias', fieldName: 'managerAlias', minWidth: 100, maxWidth: 130 },
  { key: 'hrcName', name: 'HRC Name', fieldName: 'hrcName', minWidth: 100, maxWidth: 140 },
  { key: 'hrcAlias', name: 'HRC Alias', fieldName: 'hrcAlias', minWidth: 90, maxWidth: 120 },
  { key: 'crmCaseId', name: 'CRM Case #', fieldName: 'crmCaseId', minWidth: 110, maxWidth: 140 },
  { key: 'lpmStatus', name: 'LPM Case Status', fieldName: 'lpmStatus', minWidth: 110, maxWidth: 140 },
] as const;

export const PBTWorkflowSteps = [
  'CRM Sync',
  'Details Entry',
  'Data Gathering',
  'Date & Attestation Pending',
  'Term Email & Docs',
  'Offboarding',
] as const;

export const PBTCaseDetailTabs = ['Case Details', 'Employee Data', 'Workflow Actions', 'Audit Log'] as const;

// Bulk selection constants and messages
export const MAX_BULK_SELECTION = 100;
export const BulkSelectionMessages = {
  maxReached: `Maximum ${MAX_BULK_SELECTION} cases can be selected at once.`,
  maxReachedSelectAll: `Maximum ${MAX_BULK_SELECTION} cases can be selected at once. Only the first ${MAX_BULK_SELECTION} were selected.`,
  maxReachedDeselect: `Maximum ${MAX_BULK_SELECTION} cases can be selected at once. Deselect cases to add others.`,
  selectAtLeastOne: 'Please select at least one case.',
  setAllDefaults: 'Please set all three global defaults.',
  uploadCsv: 'Please upload a CSV file.',
  uploadExcel: 'Please choose an Excel (.xlsx) file to upload.',
  excelOnly: 'Only Excel (.xlsx) files are supported. Please upload the provided template.',
  excelUploading: 'Uploading and processing the Excel file...',
  excelHint: 'The uploaded Excel file is processed by the system. Each row is validated and created independently.',
  excelResultTitle: 'Excel Upload Complete',
  excelDownloadReport: 'Download error report',
  csvDefaultsHint: 'Defaults fill in any blank fields in the CSV. Per-row values take precedence.',
  bulkCreating: 'Creating PBT cases...',
  statusAlreadyExists: 'Already Exists',
  statusAvailable: 'Available',
  fieldRequired: 'This field is required.',
  csvRowsMissing: 'row(s) missing required fields. Set global defaults to fill blanks.',
  confirmationTitle: 'Bulk Creation Complete',
  confirmationSuccess: 'case(s) created successfully.',
  confirmationFailure: 'case(s) failed to create.',
  confirmationAllSuccess: 'All cases were created successfully!',
  retryLabel: 'Retry Failed',
  doneLabel: 'Done',
} as const;

// ── Pause / Cancel Reason Options ──────────────────────────────────────────

export const PBTPauseReasonOptions = [
  { key: 'GER/WIT Review in Progress', text: 'GER/WIT Review in Progress' },
  { key: 'LOA Review in Progress', text: 'LOA Review in Progress' },
  { key: 'Legal Hold / CELA Review', text: 'Legal Hold / CELA Review' },
  { key: 'Manager Scheduling Delay', text: 'Manager Scheduling Delay' },
  { key: 'Benefits/Immigration Consultation Pending', text: 'Benefits/Immigration Consultation Pending' },
  { key: 'Other', text: 'Other' },
] as const;

export const PBTCancelReasonOptions = [
  { key: 'GER/WIT Review in Progress', text: 'GER/WIT Review in Progress' },
  { key: 'LOA Review in Progress', text: 'LOA Review in Progress' },
  { key: 'Legal Hold / CELA Review', text: 'Legal Hold / CELA Review' },
  { key: 'Manager Scheduling Delay', text: 'Manager Scheduling Delay' },
  { key: 'Benefits/Immigration Consultation Pending', text: 'Benefits/Immigration Consultation Pending' },
  { key: 'Other', text: 'Other' },
] as const;

// Minimum meaningful length (after trimming) for the free-text "Other" notes on the pause/cancel
// dialogs. Mirrors the API guard (PmConstants.CaseActionOtherReasonMinLength) so the UI blocks a
// too-short justification before the request is ever sent.
export const CaseActionNotesMinLength = 10;
